// ─────────────────────────────────────────────────────────────────────
// SOUND — audio + speech cues for the Banepad/timer screens. Pure
// browser-API side effects (Web Audio + SpeechSynthesis); no domain logic.
// Extracted verbatim from the former monolith (legacy/App.jsx) in PR-AL.
// ─────────────────────────────────────────────────────────────────────

export function speakWarning(text: string) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    // Stop any ongoing utterances first
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'en-US';
    utter.rate = 0.95; // a little slower for clarity
    utter.pitch = 1.0;
    utter.volume = 1.0;
    window.speechSynthesis.speak(utter);
  } catch (e) {
    console.warn('[Speech] Tale-syntese feilet:', e);
  }
}

export function playEndSound(soundType: string = 'alarm') {
  if (soundType === 'off') return;

  try {
    const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();

    // Helper: play one tone with an envelope (attack + decay) for a smoother sound
    const playTone = (freq, startTime, duration, volume = 0.3) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + startTime);
      gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + startTime + 0.01);
      gain.gain.setValueAtTime(volume, ctx.currentTime + startTime + duration - 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + startTime + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + startTime);
      osc.stop(ctx.currentTime + startTime + duration);
    };

    // Helper: tone with a pitch glissando (frequency transition) for the siren
    const playSweep = (fromFreq, toFreq, startTime, duration, volume = 0.3) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(fromFreq, ctx.currentTime + startTime);
      osc.frequency.linearRampToValueAtTime(toFreq, ctx.currentTime + startTime + duration);
      gain.gain.setValueAtTime(0, ctx.currentTime + startTime);
      gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + startTime + 0.02);
      gain.gain.setValueAtTime(volume, ctx.currentTime + startTime + duration - 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + startTime + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + startTime);
      osc.stop(ctx.currentTime + startTime + duration);
    };

    switch (soundType) {
      case 'alarm':
        // Classic alarm-clock alarm: alternating tone pairs over 4 sec
        // Switch between 880Hz and 1100Hz quickly, ~10 alternations in total
        for (let i = 0; i < 12; i++) {
          const freq = i % 2 === 0 ? 880 : 1100;
          playTone(freq, i * 0.18, 0.17, 0.35);
        }
        break;

      case 'siren':
        // Police/ambulance-like siren: glissando up and down over 3 cycles
        for (let i = 0; i < 4; i++) {
          playSweep(440, 880, i * 0.6, 0.3, 0.3);
          playSweep(880, 440, i * 0.6 + 0.3, 0.3, 0.3);
        }
        break;

      case 'bell':
        // Bell-like: one tone with harmonics, repeated 3 times
        for (let i = 0; i < 3; i++) {
          // Fundamental
          playTone(880, i * 0.8, 0.7, 0.25);
          // Overtones for a bell-like character
          playTone(1760, i * 0.8, 0.5, 0.15);
          playTone(2640, i * 0.8, 0.3, 0.08);
        }
        break;

      case 'whistle':
        // Football-referee whistle. Characteristics of a real pea whistle (ball inside):
        //   - High base frequency ~2500-2800 Hz (same as most sports whistles)
        //   - FAST trill (~30 Hz tremolo) from the ball rolling inside — this is
        //     what makes the sound "sharp" and recognizable as a referee whistle
        //   - White noise layered on top for the "air" character of the metal housing
        //   - Pattern: 2 short blasts + 1 long (classic kickoff whistle from football)
        {
          const playWhistle = (startTime, duration) => {
            const baseFreq = 2700; // referee-whistle frequency
            const tremoloFreq = 32; // trill from the ball, fast — ~32 Hz
            const tremoloDepth = 180; // deep modulation for a sharp sound

            // Main oscillator: square gives more harmonics and a "sharper" sound
            // than sine/triangle, which matches a real pea whistle
            const osc = ctx.createOscillator();
            osc.type = 'square';
            osc.frequency.value = baseFreq;

            // LFO that creates the trill — modulates pitch quickly
            const lfo = ctx.createOscillator();
            const lfoGain = ctx.createGain();
            lfo.type = 'sine';
            lfo.frequency.value = tremoloFreq;
            lfoGain.gain.value = tremoloDepth;
            lfo.connect(lfoGain);
            lfoGain.connect(osc.frequency);

            // Bandpass filter around the base frequency — strips away sub-bass
            // and higher harmonics that don't belong in a whistle
            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.value = baseFreq;
            filter.Q.value = 4;

            // White noise for the "air" character — short buffer that loops
            const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
            const noiseData = noiseBuffer.getChannelData(0);
            for (let i = 0; i < noiseData.length; i++) noiseData[i] = (Math.random() * 2 - 1) * 0.5;
            const noise = ctx.createBufferSource();
            noise.buffer = noiseBuffer;
            noise.loop = true;
            const noiseFilter = ctx.createBiquadFilter();
            noiseFilter.type = 'bandpass';
            noiseFilter.frequency.value = baseFreq;
            noiseFilter.Q.value = 3;

            // Envelope — very fast attack (whistle starts immediately),
            // flat sustain, short decay
            const oscGain = ctx.createGain();
            oscGain.gain.setValueAtTime(0, ctx.currentTime + startTime);
            oscGain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + startTime + 0.015);
            oscGain.gain.setValueAtTime(0.4, ctx.currentTime + startTime + duration - 0.05);
            oscGain.gain.linearRampToValueAtTime(0, ctx.currentTime + startTime + duration);

            const noiseGain = ctx.createGain();
            noiseGain.gain.setValueAtTime(0, ctx.currentTime + startTime);
            noiseGain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + startTime + 0.015);
            noiseGain.gain.setValueAtTime(0.18, ctx.currentTime + startTime + duration - 0.05);
            noiseGain.gain.linearRampToValueAtTime(0, ctx.currentTime + startTime + duration);

            osc.connect(filter).connect(oscGain).connect(ctx.destination);
            noise.connect(noiseFilter).connect(noiseGain).connect(ctx.destination);

            osc.start(ctx.currentTime + startTime);
            lfo.start(ctx.currentTime + startTime);
            noise.start(ctx.currentTime + startTime);

            const stopTime = ctx.currentTime + startTime + duration + 0.05;
            osc.stop(stopTime);
            lfo.stop(stopTime);
            noise.stop(stopTime);
          };

          // Classic pattern: 2 short blasts + 1 long (like a referee starting the match)
          playWhistle(0, 0.22); // blast 1
          playWhistle(0.32, 0.22); // blast 2
          playWhistle(0.7, 1.1); // long whistle
        }
        break;

      case 'beeps':
        // Three short beeps — the original simple variant
        playTone(880, 0, 0.18, 0.3);
        playTone(880, 0.25, 0.18, 0.3);
        playTone(880, 0.5, 0.18, 0.3);
        break;

      default:
        // Unknown sound → fall back to alarm
        for (let i = 0; i < 12; i++) {
          const freq = i % 2 === 0 ? 880 : 1100;
          playTone(freq, i * 0.18, 0.17, 0.35);
        }
    }
  } catch (e) {
    console.warn('[Audio] Kunne ikke spille lyd:', e);
  }
}
